import React, { useState, useEffect } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Hidden from "@material-ui/core/Hidden";
import Typography from "@material-ui/core/Typography";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import Slide from "@material-ui/core/Slide";
import { Chip } from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { Dialog, DialogContent, DialogActions } from "@material-ui/core";
import LeaveConfirmationModal from "./LeaveConfirmationModal";
import Logo from "./Logo";
import Controls from "./Controls";
import Scene from "./Scene";
import Loader from "./Loader";
import SummaryView from "./Summary";
import Upload from "./Upload";
import KeyboardCoverOverlay from "./KeyboardCoverOverlay";
import {
  StyledHeaderActions,
  StyledVerticalSeparator,
  StyledCircleButton,
  StyledRedirectToSummaryButton,
  StyledSummaryLoader,
  StyledBgrdLoader,
  SummaryViewWrapper,
  StyledPreviewModeButton,
} from "./common.styles";
import models, { FRONT_SIDE, BACK_SIDE, LEFT_SIDE, RIGHT_SIDE } from "./models";

const sideSelectionMap = {
  0: BACK_SIDE,
  1: RIGHT_SIDE,
  2: FRONT_SIDE,
  3: LEFT_SIDE,
};

const backgrounds = [
  {
    name: "Default",
    image: null,
  },
  {
    name: "Background 1",
    image: require("../../assets/backgrounds/Indoor1.jpg"),
  },
  {
    name: "Background 2",
    image: require("../../assets/backgrounds/Indoor2.jpg"),
  },
  {
    name: "Background 3",
    image: require("../../assets/backgrounds/Indoor3.jpg"),
  },
  {
    name: "Background 4",
    image: require("../../assets/backgrounds/Indoor4.jpg"),
  },
  {
    name: "Background 5",
    image: require("../../assets/backgrounds/Indoor5.jpg"),
  },
  {
    name: "Background 6",
    image: require("../../assets/backgrounds/Outdoor1.jpg"),
  },
  {
    name: "Background 7",
    image: require("../../assets/backgrounds/Outdoor2.jpg"),
  },
  {
    name: "Background 8",
    image: require("../../assets/backgrounds/Outdoor3.jpg"),
  },
  {
    name: "Background 9",
    image: require("../../assets/backgrounds/Outdoor4.jpg"),
  },
];

const CreatorComponent = ({ history, match: { params } }) => {
  const currentModel = params.model || "";
  const submodel = params.submodel || "";

  if (
    !models[currentModel] ||
    (models[currentModel].submodels &&
      !models[currentModel].submodels[submodel])
  ) {
    return <Typography>Ups, this model does not exists</Typography>;
  }

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingBgrd, setLoadingBgrd] = useState(false);
  const [summaryViewsTimestamp, setSummaryViewsTimestamp] = useState(0);
  const [selectedBackground, setSelectedBackground] = useState(0);
  const [uploadedBgrd, setUploadedBgrd] = useState(null);
  const open = Boolean(anchorEl);

  const [previewMode, setPreviewMode] = useState(false);
  const [focusedSidePreviewImage, setFocusedSidePreviewImage] = useState(null);
  const [showFullscreenPreview, setShowFullscreenPreview] = useState(false);
  const [selectedLogoImages, setSelectedLogoImages] = useState({});
  const [wrapImage, setWrapImage] = useState(null);
  const [selectedFocusSide, setSelectedFocusSide] = useState(0);
  const [selectedSwatches, setSelectedSwatches] = useState({
    color: {},
    pattern: {},
  });
  const [generatedModel, setGeneratedModel] = useState(null);
  const [showGeneratedModel, setShowGeneratedModel] = useState(false);
  const [leaveConfirmationModalOpen, setLeaveConfirmationModalOpen] = useState(
    false
  );

  useEffect(() => {
    (async () => {
      const wasInfoModalDisplayed = localStorage.getItem("INFO_MODAL");
      if (!wasInfoModalDisplayed) {
        setShowInfoModal(true);
      }
    })();
  });

  const handleInfoModalClose = () => {
    localStorage.setItem("INFO_MODAL", true);
    setShowInfoModal(false);
  };

  const onMessage = async (e) => {
    if (!e.data) {
      await setShowFullscreenPreview(false);
    }

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(showFullscreenPreview);
    }
  };

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);

  const selectedBgrdUrl = uploadedBgrd
    ? uploadedBgrd
    : backgrounds[selectedBackground].image;

  const handleColorSelection = (color) => {
    setSelectedSwatches({
      ...selectedSwatches,
      color: {
        ...selectedSwatches.color,
        [sideSelectionMap[selectedFocusSide]]: color,
      },
    });
  };

  const updateLogoTexture = (index, coords, file) => {
    setSelectedLogoImages({ ...selectedLogoImages, [index]: { coords, file } });
  };

  const updateWrapTexture = (file) => {
    setWrapImage(file);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const generateSummaryViewTimestamps = () => {
    setLoading(true);
    // to improve UX
    setTimeout(() => setSummaryViewsTimestamp(Date.now()), 1000);
  };

  const handleSelectBackground = (index) => {
    setLoadingBgrd(true);
    setUploadedBgrd(null);
    setSelectedBackground(index);
    // to improve UX
    setTimeout(() => setLoadingBgrd(false), 1500);
  };

  const onModelLoaded = (model) => {
    setLoading(false);
  };

  const onModelFocus = (modelPreviewImg) => {
    setFocusedSidePreviewImage(modelPreviewImg);
  };

  const redirectToSummaryView = (images) => {
    setGeneratedModel({
      model: submodel
        ? models[currentModel].submodels[submodel]
        : models[currentModel],
      images,
      backgroundImage: selectedBgrdUrl,
    });
    setShowGeneratedModel(true);

    // to improve UX
    setTimeout(() => setLoading(false), 500);
  };

  return (
    // TODO - cleanup styles
    <Box
      width="100%"
      height="100%"
      style={{
        backgroundImage: previewMode ? `url(${selectedBgrdUrl})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
        backgroundRepeat: "no-repeat",
        backgroundColor:
          previewMode && !backgrounds[selectedBackground].image
            ? "#f3f3f3"
            : "transparent",
      }}
    >
      <StyledHeaderActions inverted={previewMode} autoWidth={!previewMode}>
        <Hidden smDown>
          <Typography variant="h1">
            {submodel
              ? models[currentModel].submodels[submodel].name
              : currentModel.toUpperCase()}
          </Typography>
        </Hidden>
        {!previewMode && (
          <>
            <Hidden lgUp>
              <Box mr={1} flex="1" />
            </Hidden>
            <Hidden smDown>
              <Box mr={1} />
            </Hidden>
            <Chip
              onClick={() => setLeaveConfirmationModalOpen(true)}
              label="Start Over"
              color="secondary"
            />
            <LeaveConfirmationModal
              modalOpen={leaveConfirmationModalOpen}
              onConfirm={() => history.push("/")}
              onClose={() => setLeaveConfirmationModalOpen(false)}
            />
          </>
        )}
        {previewMode && !showGeneratedModel && (
          <>
            <Box flex="1" />
            <Chip
              disabled={loadingBgrd}
              onClick={handleClick}
              label="Change background"
              color="secondary"
              avatar={
                <Box
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundImage: previewMode
                      ? `url(${
                          uploadedBgrd
                            ? uploadedBgrd
                            : backgrounds[selectedBackground].image
                        })`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    borderRadius: "50%",
                    backgroundColor:
                      previewMode && !backgrounds[selectedBackground].image
                        ? "#f3f3f3"
                        : "transparent",
                  }}
                />
              }
            />
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              {backgrounds.map((background, index) => {
                return (
                  <MenuItem
                    onClick={() => {
                      handleSelectBackground(index);
                      handleClose();
                    }}
                  >
                    {background.name}
                  </MenuItem>
                );
              })}
              <label htmlFor="contained-button-file">
                <MenuItem onClick={() => {}}>
                  <Upload
                    plainButton
                    onlyHorizontal
                    NoteComponent={
                      <Typography variant="caption">
                        Only <b>landscape oriented</b> images are accepted.
                        <br />
                        Only <b>JPG</b> and <b>PNG</b> formats are supported.
                      </Typography>
                    }
                    label="Upload Custom"
                    onClose={handleClose}
                    onSelect={(img) => {
                      handleClose();
                      setUploadedBgrd(URL.createObjectURL(img));
                    }}
                  />
                </MenuItem>
              </label>
            </Menu>
            <Box mr={1} />
            <Hidden smUp>
              <StyledCircleButton onClick={() => setPreviewMode(false)}>
                <img src={require("../../assets/close.png")} />
              </StyledCircleButton>
            </Hidden>
            <Hidden smDown>
              <Chip
                color="secondary"
                label="Back to Editor"
                onClick={() => setPreviewMode(false)}
              />
            </Hidden>
          </>
        )}
      </StyledHeaderActions>
      <Box display="flex" flexDirection="row" height="100%">
        <Box flex="1" position="relative">
          {/* TODO extract to reusable vars */}
          <Scene
            model={
              submodel
                ? models[currentModel].submodels[submodel]
                : models[currentModel]
            }
            selectedSwatches={selectedSwatches}
            availableSides={
              submodel
                ? models[currentModel].submodels[submodel].sides
                : models[currentModel].sides
            }
            selectedFocusCoordinates={
              submodel
                ? models[currentModel].submodels[submodel].sides[
                    sideSelectionMap[selectedFocusSide]
                  ]
                : models[currentModel].sides[
                    sideSelectionMap[selectedFocusSide]
                  ]
            }
            selectedFocusSide={selectedFocusSide}
            selectedLogoImages={selectedLogoImages}
            wrapImage={wrapImage}
            summaryViewsTimestamp={summaryViewsTimestamp}
            onSummaryViewsGenerated={redirectToSummaryView}
            onModelLoaded={onModelLoaded}
            onFocus={onModelFocus}
          />
        </Box>
        <Controls
          // don't unmount to preserve internal state
          hidden={previewMode}
          onSideSelection={(side) => setSelectedFocusSide(side)}
          onPreviewModeSet={(state) => {
            setPreviewMode(state);
          }}
          selectedFocusCoordinates={
            submodel
              ? models[currentModel].submodels[submodel].sides[
                  sideSelectionMap[selectedFocusSide]
                ]
              : models[currentModel].sides[sideSelectionMap[selectedFocusSide]]
          }
          onColorSelection={handleColorSelection}
          selectedLogoImages={selectedLogoImages}
          updateLogoTexture={updateLogoTexture}
          wrapImage={wrapImage}
          updateWrapTexture={updateWrapTexture}
          initializing={loading}
        />
        {previewMode && (
          <>
            <StyledRedirectToSummaryButton>
              <Chip
                onClick={generateSummaryViewTimestamps}
                label="Go to summary view"
                color="secondary"
                disabled={loadingBgrd}
              />
            </StyledRedirectToSummaryButton>
            <Hidden lgUp>
              {selectedBgrdUrl && (
                <StyledPreviewModeButton
                  color="primary"
                  onClick={() => {
                    if (window.ReactNativeWebView) {
                      window.ReactNativeWebView.postMessage(
                        !showFullscreenPreview
                      );
                    }
                    setShowFullscreenPreview(!showFullscreenPreview);
                  }}
                >
                  {showFullscreenPreview ? (
                    <FullscreenExitIcon />
                  ) : (
                    <FullscreenIcon />
                  )}
                </StyledPreviewModeButton>
              )}
            </Hidden>
          </>
        )}
      </Box>
      {previewMode && loading && (
        <StyledSummaryLoader>
          <Loader description="Generating your robot" />
        </StyledSummaryLoader>
      )}
      {previewMode && loadingBgrd && (
        <StyledBgrdLoader>
          <Loader description="Rendering new background" />
        </StyledBgrdLoader>
      )}

      <Slide
        direction="up"
        in={Boolean(generatedModel) && showGeneratedModel}
        mountOnEnter
        unmountOnExit
        timeout={450}
      >
        <SummaryViewWrapper>
          <SummaryView
            onClose={() => {
              setShowGeneratedModel(false);
              setPreviewMode(false);
            }}
            location={{
              state: generatedModel,
            }}
            history={history}
          />
        </SummaryViewWrapper>
      </Slide>
      <Slide
        direction="up"
        in={showFullscreenPreview}
        mountOnEnter
        unmountOnExit
        timeout={450}
      >
        <SummaryViewWrapper dark className="centered">
          <Box width="100%" position="relative">
            <img src={selectedBgrdUrl} />
            <img src={focusedSidePreviewImage} />
          </Box>
        </SummaryViewWrapper>
      </Slide>
      {!showGeneratedModel && <KeyboardCoverOverlay />}

      <Dialog open={showInfoModal} onClose={handleInfoModalClose}>
        <DialogContent>
          <Box textAlign="center" mt={1} mb={1} pl={1} pr={1}>
            <Box mb={1} />
            <Typography variant="h5">How This Works</Typography>
            <Box mb={1} />
            <Typography variant="caption">
              Create your robot as you would like!
            </Typography>
            <Box mb={0.5} />

            <Typography variant="caption">
              You are able to add your logo, colors or wrap to your custom
              robot.
            </Typography>
            <Box mb={0.5} />
            <Typography variant="caption">
              Knightscope will review your design and once approved, you will
              receive an email with the final images.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box flex="2" />
          <Button
            variant="contained"
            color="primary"
            onClick={handleInfoModalClose}
          >
            Ok
          </Button>
          <Box flex="2" />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatorComponent;
